<template>
  <div
    v-if="visible && details && details.length"
    class="driver-actuals-booking-panel"
    :class="{ 'driver-actuals-booking-panel--left': leftAlignLabels, 'driver-actuals-booking-panel--inline': inline }"
    :style="inline ? null : panelStyle"
    @mouseenter="$emit('panel-enter')"
    @mouseleave="$emit('panel-leave')"
  >
    <div v-if="!inline" class="driver-actuals-booking-panel-header">{{ panelHeader }}</div>
    <div
      v-for="(detail, idx) in details"
      :key="detail.key"
      class="driver-actuals-booking-detail"
      :class="{ 'driver-actuals-booking-detail--sep': idx > 0 }"
    >
      <table v-if="detail.is_service_event" class="driver-actuals-booking-detail-table">
        <tbody>
          <tr>
            <th>Event</th>
            <td>{{ detail.event_type || '—' }}</td>
          </tr>
          <tr v-if="detail.comment">
            <th>Notes</th>
            <td>{{ detail.comment }}</td>
          </tr>
          <tr>
            <th>Start</th>
            <td>{{ detail.planned_start }}</td>
          </tr>
          <tr>
            <th>Finish</th>
            <td>{{ detail.planned_finish }}</td>
          </tr>
        </tbody>
      </table>
      <table v-else class="driver-actuals-booking-detail-table">
        <tbody>
          <tr>
            <th>{{ charterLabel }}</th>
            <td>
              <!-- planned-schedule views link straight to the booking on the
                   actuals page; new tab so the board keeps its place -->
              <a v-if="detail.actuals_href" class="driver-actuals-booking-link" :href="detail.actuals_href" target="_blank" rel="noopener">{{ detail.charter_id || '—' }}&nbsp;<i class="fa fa-external-link" aria-hidden="true"></i></a>
              <template v-else>{{ detail.charter_id || '—' }}</template>
            </td>
          </tr>
          <tr v-if="showDriverRow">
            <th>Driver</th>
            <td>{{ detail.driver || '—' }}</td>
          </tr>
          <tr v-if="showVehicleRow">
            <th>Vehicle ID</th>
            <td>{{ detail.vehicle_id || '—' }}</td>
          </tr>
          <tr>
            <th>Planned Start</th>
            <td>{{ detail.planned_start }}</td>
          </tr>
          <tr>
            <th>Planned Finish</th>
            <td>{{ detail.planned_finish }}</td>
          </tr>
          <tr v-if="showDetailField(detail, 'actual_start')">
            <th>{{ actualStartLabel }}</th>
            <td>{{ detail.actual_start }}</td>
          </tr>
          <tr v-if="showDetailField(detail, 'actual_finish')">
            <th>{{ actualFinishLabel }}</th>
            <td>{{ detail.actual_finish }}</td>
          </tr>
          <tr v-if="showDetailField(detail, 'planned_mileage')">
            <th>Planned Mileage</th>
            <td>{{ detail.planned_mileage }}</td>
          </tr>
          <tr v-if="showDetailField(detail, 'actual_mileage')">
            <th>Actual Mileage</th>
            <td>{{ detail.actual_mileage }}</td>
          </tr>
          <!-- full-detail rows (the click modal's actuals-page field set) -->
          <tr v-for="(extra, ridx) in detail.extra_rows || []" :key="'extra-' + ridx">
            <th>{{ extra.label }}</th>
            <td>{{ extra.value }}</td>
          </tr>
          <tr v-if="detail.status === 'error' && detail.status_msg">
            <th>Error</th>
            <td
              class="driver-actuals-booking-error-msg"
              v-html="detail.status_msg"
            />
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'driver_actuals_booking_panel',

  props: {
    visible: { type: Boolean, default: false },
    details: { type: Array, default: () => [] },
    panelStyle: { type: Object, default: () => ({}) },
    variant: { type: String, default: 'cm' },
    resourceType: { type: String, default: 'driver' },
    headerLabel: { type: String, default: 'Booking Details' },
    leftAlignLabels: { type: Boolean, default: false },
    // renders the detail tables in flow (no fixed positioning, header, or
    // chrome) for embedding inside a modal body
    inline: { type: Boolean, default: false },
  },

  computed: {
    panelHeader() {
      if (this.details && this.details.length && this.details.every(d => d.is_service_event))
        return 'Maintenance Event'
      return this.headerLabel
    },
    charterLabel() {
      return this.variant === 'tbn' ? 'Booking ID' : 'Charter ID'
    },
    showDriverRow() {
      return this.resourceType === 'vehicle'
    },
    showVehicleRow() {
      return this.resourceType === 'driver'
    },
    actualStartLabel() {
      if (this.resourceType === 'vehicle')
        return this.variant === 'tbn' ? 'Actual Vehicle Leave' : 'Actual Vehicle Start'
      return this.variant === 'tbn' ? 'Actual Clock In' : 'Actual HOS On'
    },
    actualFinishLabel() {
      if (this.resourceType === 'vehicle')
        return this.variant === 'tbn' ? 'Actual Vehicle Back' : 'Actual Vehicle Finish'
      return this.variant === 'tbn' ? 'Actual Clock Out' : 'Actual HOS Off'
    },
  },

  methods: {
    showDetailField(detail, field) {
      if (!detail)
        return false
      const value = detail[field]
      if (value == null)
        return false
      if (detail.status === 'error')
        return value !== '' && value !== '—'
      return true
    },
  },
}
</script>

<style scoped>
.driver-actuals-booking-panel {
  position: fixed;
  z-index: 2000;
  max-width: 380px;
  max-height: 70vh;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #6c757d;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  padding: 0;
  pointer-events: auto;
}
.driver-actuals-booking-panel-header {
  font-weight: bold;
  font-size: 15px;
  padding: 10px 12px;
  background: #eee;
  border-bottom: 1px solid #ccc;
}
.driver-actuals-booking-detail {
  padding: 10px 12px;
}
.driver-actuals-booking-detail--sep {
  border-top: 1px solid #dee2e6;
}
.driver-actuals-booking-detail-table {
  width: 100%;
  font-size: 13px;
}
.driver-actuals-booking-detail-table th {
  text-align: right;
  padding: 3px 10px 3px 0;
  vertical-align: top;
  white-space: nowrap;
  font-weight: 600;
  color: #495057;
}
.driver-actuals-booking-detail-table td {
  text-align: left;
  padding: 3px 0;
  word-break: break-word;
}
.driver-actuals-booking-panel--inline {
  position: static;
  max-width: none;
  max-height: none;
  border: none;
  box-shadow: none;
  border-radius: 0;
}
.driver-actuals-booking-link {
  font-weight: 600;
}
.driver-actuals-booking-link .fa-external-link {
  font-size: 11px;
}
.driver-actuals-booking-error-msg {
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}
.driver-actuals-booking-panel--left .driver-actuals-booking-detail-table th {
  text-align: left;
}
</style>
